import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Button,
  Alert,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Paper,
  Skeleton,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Add,
  GridView,
  CalendarToday,
  Refresh,
  Schedule as ScheduleIcon,
  PlayArrow,
  Stop,
  Warning,
  CheckCircle,
  Edit,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import {
  ScheduleStatus,
  Priority,
  ScheduleType,
  EventStatus,
  type Schedule,
  type SearchFilters,
  type SortOptions,
  type PaginatedResponse
} from "../../types/index";
import apiClient from '../../services/api';
import {
  ScheduleCard,
  ScheduleFilters,
  ScheduleDialog,
  ScheduleCalendarView,
  ScheduleDetailsDialog
} from '../../components/schedule';
import { invalidateScheduleData } from '../../utils/cacheInvalidation';

const SchedulePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canManageSchedules } = useRole();
  const queryClient = useQueryClient();

  // Состояния для UI
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Состояния для модального диалога
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | undefined>();
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsSchedule, setDetailsSchedule] = useState<Schedule | null>(null);

  // Состояния для фильтрации и сортировки
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    scheduleStatuses: [],
    priorities: [],
  });

  // Состояние для сортировки
  const [sortOptions, setSortOptions] = useState({
    field: 'start_date',
    direction: 'desc' as 'asc' | 'desc'
  });

  // Запросы к API
  const { data: scheduleResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['schedules', currentPage, pageSize, filters, sortOptions],
    queryFn: async (): Promise<PaginatedResponse<Schedule>> => {
      return await apiClient.getSchedules(filters, sortOptions, currentPage, pageSize);
    },
    refetchInterval: 30000, // Обновляем каждые 30 секунд
    staleTime: 10000, // Данные считаются свежими 10 секунд
  });

  const { data: scheduleStats } = useQuery({
    queryKey: ['schedule-stats'],
    queryFn: () => apiClient.getScheduleStats(),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  const { data: playlistResponse } = useQuery({
    queryKey: ['schedule-filter-playlists'],
    queryFn: () => apiClient.getPlaylists(undefined, { field: 'name', direction: 'asc' }, 1, 100),
    staleTime: 60000,
  });

  const { data: deviceResponse } = useQuery({
    queryKey: ['schedule-filter-devices'],
    queryFn: () => apiClient.getDevices({ page: 1, size: 100, sort_by: 'name', sort_order: 'asc' }),
    staleTime: 60000,
  });

  const availableAuthors = React.useMemo(() => {
    const authors = new Map<number, string>();
    scheduleResponse?.items.forEach((schedule) => {
      if (schedule.creator) {
        authors.set(schedule.creator.id, schedule.creator.full_name || schedule.creator.username);
      }
    });
    return Array.from(authors.entries()).map(([id, name]) => ({ id, name }));
  }, [scheduleResponse?.items]);

  // Мутации
  const createScheduleMutation = useMutation({
    mutationFn: apiClient.createSchedule,
    onSuccess: (newSchedule) => {
      invalidateScheduleData(queryClient);
      setSnackbar({
        open: true,
        message: t('schedule.createSuccess', { name: newSchedule.name }),
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('common.error'),
        severity: 'error',
      });
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.updateSchedule(id, data),
    onSuccess: (updatedSchedule) => {
      invalidateScheduleData(queryClient);
      setSnackbar({
        open: true,
        message: t('schedule.updateSuccess', { name: updatedSchedule.name }),
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('common.error'),
        severity: 'error',
      });
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteSchedule(id),
    onSuccess: () => {
      invalidateScheduleData(queryClient);
      setSnackbar({
        open: true,
        message: t('schedule.deleteSuccess'),
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('common.error'),
        severity: 'error',
      });
    },
  });

  const startScheduleMutation = useMutation({
    mutationFn: (id: number) => apiClient.startSchedule(id),
    onSuccess: async (result) => {
      await invalidateScheduleData(queryClient);
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || t('common.error'),
        severity: 'error',
      });
    },
  });

  const stopScheduleMutation = useMutation({
    mutationFn: (id: number) => apiClient.stopSchedule(id),
    onSuccess: async (result) => {
      await invalidateScheduleData(queryClient);
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || t('common.error'),
        severity: 'error',
      });
    },
  });

  // Обработчики событий
  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      scheduleStatuses: [],
      priorities: [],
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewModeChange = (newMode: 'list' | 'calendar') => {
    setViewMode(newMode);
  };

  // Действия с расписанием
  const handleCreateSchedule = () => {
    setEditingSchedule(undefined);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleDuplicateSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setDialogMode('duplicate');
    setDialogOpen(true);
  };

  const handleDeleteSchedule = (schedule: Schedule) => {
    if (window.confirm(t('schedule.confirmDelete', { name: schedule.name }))) {
      deleteScheduleMutation.mutate(schedule.id);
    }
  };

  const handleSaveSchedule = async (_schedule: Schedule) => {
    await invalidateScheduleData(queryClient);
  };

  const handleScheduleStart = (schedule: Schedule) => {
    startScheduleMutation.mutate(schedule.id);
  };

  const handleScheduleStop = (schedule: Schedule) => {
    stopScheduleMutation.mutate(schedule.id);
  };

  const handleResolveConflicts = (schedule: Schedule) => {
    setDetailsSchedule(schedule);
    setDetailsOpen(true);
    setSnackbar({
      open: true,
      message: 'Открыл детали расписания. Конфликты отображаются и исправляются на уровне событий.',
      severity: 'success',
    });
  };

  const handleViewDetails = (schedule: Schedule) => {
    setDetailsSchedule(schedule);
    setDetailsOpen(true);
  };

  const handleDetailsEdit = (schedule: Schedule) => {
    setDetailsOpen(false);
    handleEditSchedule(schedule);
  };

  // Загрузочные скелетоны
  const renderSkeletons = () => (
    <Grid container spacing={2}>
      {Array.from({ length: pageSize }).map((_, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
          <Paper elevation={1} sx={{ p: 2, height: 400 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" height={24} width="80%" />
                <Skeleton variant="text" height={16} width="60%" />
              </Box>
            </Box>
            <Skeleton variant="text" height={16} width="40%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={16} width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={16} width="50%" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  // Отображение расписания
  const renderScheduleGrid = () => {
    if (!scheduleResponse?.items.length) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('schedule.noSchedules')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('schedule.noSchedulesDescription')}
          </Typography>
          {canManageSchedules() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateSchedule}
            >
              {t('schedule.createFirst')}
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {scheduleResponse.items.map((schedule) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={schedule.id}>
            <ScheduleCard
              schedule={schedule}
              currentUserRole={user?.role}
              onPlay={handleScheduleStart}
              onStop={handleScheduleStop}
              onEdit={canManageSchedules() ? handleEditSchedule : undefined}
              onDuplicate={canManageSchedules() ? handleDuplicateSchedule : undefined}
              onDelete={canManageSchedules() ? handleDeleteSchedule : undefined}
              onResolveConflicts={canManageSchedules() ? handleResolveConflicts : undefined}
              onViewDetails={handleViewDetails}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      {/* Заголовок страницы */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            {t('navigation.schedule')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('schedule.pageDescription')}
          </Typography>

          {/* Быстрая статистика */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<PlayArrow />}
              label={`${scheduleStats?.active_schedules || 0} ${t('schedule.status.active')}`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<ScheduleIcon />}
              label={`${scheduleStats?.scheduled_events || 0} ${t('schedule.status.scheduled')}`}
              color="info"
              variant="outlined"
            />
            <Chip
              icon={<Warning />}
              label={`${scheduleStats?.failed_events || 0} ${t('schedule.status.conflict')}`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<CheckCircle />}
              label={`${scheduleStats?.completed_events || 0} ${t('schedule.status.completed')}`}
              color="success"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Кнопка обновления */}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {t('common.refresh')}
          </Button>

          {/* Кнопка создания */}
          {canManageSchedules() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateSchedule}
            >
              {t('schedule.createSchedule')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Фильтры */}
      <ScheduleFilters
        filters={filters}
        sortOptions={sortOptions}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        totalItems={scheduleResponse?.total || 0}
        scheduleStats={scheduleStats}
        availableAuthors={availableAuthors}
        availablePlaylists={(playlistResponse?.items || []).map((playlist) => ({ id: playlist.id, name: playlist.name }))}
        availableDevices={(deviceResponse?.items || []).map((device) => ({ id: device.id, name: device.name }))}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {/* Ошибка загрузки */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.loadError')}
        </Alert>
      )}

      {/* Контент страницы */}
      {viewMode === 'calendar' ? (
        <ScheduleCalendarView
          filters={{
            search: filters.search, // Assuming searchTerm is not defined in this scope
            device_id: filters.deviceId,
            playlist_id: filters.playlistId,
            status: filters.scheduleStatuses?.[0],
            priority: filters.priorities,
            start_date: filters.dateFrom,
            end_date: filters.dateTo,
          }}
        />
      ) : (
        <>
          {isLoading ? renderSkeletons() : renderScheduleGrid()}
        </>
      )}

      {/* Пагинация */}
      {scheduleResponse && scheduleResponse.total_pages > 1 && viewMode === 'list' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={scheduleResponse.total_pages}
            page={currentPage}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* FAB для мобильных устройств */}
      {canManageSchedules() && (
        <Fab
          color="primary"
          aria-label="create schedule"
          onClick={handleCreateSchedule}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Модальный диалог */}
      <ScheduleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveSchedule}
        schedule={editingSchedule}
        mode={dialogMode}
      />

      <ScheduleDetailsDialog
        open={detailsOpen}
        schedule={detailsSchedule}
        onClose={() => setDetailsOpen(false)}
        onUpdate={handleSaveSchedule}
        onEdit={handleDetailsEdit}
      />

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulePage;

